import { FC } from 'react';
import theme from '../../../theme';
import { PlusIcon } from '../icons';
import { LoadingDots } from '../Loading';
import Button from './Button';
import { RefactoredButtonProps } from './button-props';
import { useAsyncClick } from '../hooks/useAsyncClick';

/**
 * Primary "create / proceed" control. Often rendered inside `<form onSubmit={...}>` with no `onClick`
 * (e.g. Upload Document, certificate upload, member log). Those flows rely on the native submit button:
 * when `type` is omitted, the DOM defaults to `submit` inside a form.
 *
 * Do NOT add a default `type="button"` here — that silently breaks form submission (no errors in console).
 * Pass `type="button"` only for in-form actions that must not submit; use `type="submit"` when you want
 * to be explicit (e.g. signatory verify-code).
 */
const NewButton: FC<RefactoredButtonProps & { 'data-testid'?: string; type?: 'button' | 'submit' }> = ({
  title,
  onClick,
  onClickAsync,
  enabled = true,
  hideIcon = false,
  isWorking = false,
  preventDoubleClick = true,
  'data-testid': dataTestId,
  type,
}: RefactoredButtonProps & { 'data-testid'?: string; type?: 'button' | 'submit' }) => {
  const { run, isRunning } = useAsyncClick(
    async (e?: any) => {
      if (onClickAsync) await onClickAsync(e);
    },
    { debounceMs: 500 }
  );
  const working = isWorking || (preventDoubleClick && isRunning);
  const canClick = enabled && !working;
  return (
    <Button
      size="xs"
      color={!canClick ? 'disabled' : 'primary'}
      type={type}
      onClick={(e) => (onClickAsync ? run(e) : onClick && onClick(e))}
      disabled={!canClick}
      data-testid={dataTestId}
    >
      {!hideIcon && <PlusIcon color={!canClick ? theme.colors.general.disabled : undefined} />}
      {working ? <LoadingDots color="white" /> : title}
    </Button>
  );
};

export default NewButton;
